<template>
  <PageWrapper>
    <template #content>
      <div class="container max-w-6xl mx-auto p-8">
        <div v-if="vendorKey && vendorKey !== 'all'" class="grid md:grid-cols-2 gap-4">
          <div class="flex flex-col gap-4">
            <h2 class="text-2xl font-semibold">
              Vendor Participants List Upload
            </h2>
            <p>
              Use this page to upload your Vendors Participants List. Once
              uploaded, we will process it in the next 24 hours.
            </p>
            <p>
              Note that your user account must be configured by Mirus Research Support to upload files. The file must be a
              <code class="bg-gray-100 p-1 rounded-sm font-mono">.csv</code>
              with the exact headers below.
              <blockquote class="bg-gray-100 mt-1 py-1 px-2 rounded-sm font-mono">
                Associate ID,Agent Name,Agent Alias
              </blockquote>
            </p>
            <p>
              If you would like to automate this process contact Mirus Research
              Support and we will assist you!
            </p>
          </div>
          <div class="space-y-4">
            <StatBlock
              label="last upload"
              :stat="lastUploadDate"
              :loading="!lastUploadDate"
              dense
            />
            <form @submit.prevent="uploadFile">
              <div class="flex items-center justify-center w-full">
                <label
                  for="dropzone-file"
                  class="flex flex-col items-center justify-center w-full h-64 bg-neutral-secondary-medium rounded-xl border border-dashed border-default-strong rounded-base cursor-pointer hover:bg-neutral-tertiary-medium"
                >
                  <div
                    class="flex flex-col items-center justify-center text-body pt-5 pb-6"
                  >
                    <FileArrowUp class="w-8 h-8 mb-4" />
                    <p class="mb-2 text-sm">
                      <span class="font-semibold">Click to upload</span>
                      <span> or drag and drop</span>
                    </p>
                  </div>
                  <input
                    id="dropzone-file"
                    type="file"
                    class="hidden"
                    accept=".csv"
                    @change="handleFileSelect"
                  />

                  <div v-if="file">
                    <button class="basis-1/6 h-10 mt-6 text-white" type="submit">
                      Upload "{{ file.name }}"
                    </button>
                  </div>
                  <div v-else>No file selected</div>
                </label>
              </div>
            </form>
          </div>
        </div>
        <div v-else class="text-center">
          <h2 class="mt-8 text-2xl font-semibold">No vendor selected</h2>
          <p class="mt-2">Please select a vendor in order to view this page.</p>
        </div>
      </div>
    </template>
  </PageWrapper>
</template>

<script setup>
import { useCookies } from "@vueuse/integrations/useCookies";
import { parseISO } from "date-fns";
import { storeToRefs } from "pinia";
import { nextTick, onMounted, ref } from "vue";
import FileArrowUp from "@/components/icons/FileArrowUp.vue";
import PageWrapper from "@/components/PageWrapper.vue";
import StatBlock from "@/components/StatBlock.vue";
import useNotificationStore from "@/stores/notifications";
import useUserStore from "@/stores/user";

const notificationStore = useNotificationStore();
const userStore = useUserStore();

const { vendorKey } = storeToRefs(userStore);

let file = ref(null);
const lastUploadDate = ref(null);

function handleFileSelect(event) {
  const input = event.target;
  file.value = input.files[0];
}

async function uploadFile() {
  const form = new FormData();
  form.append("file", file.value);
  const cookies = useCookies(["csrftoken"]);
  const response = await fetch(
    `/api/vendors/content/${vendorKey.value}/participants/`,
    {
      method: "POST",
      body: form,
      headers: {
        "X-CSRFToken": cookies.get("csrftoken"),
      },
    },
  );

  if (response.ok) {
    notificationStore.addNotification(`File upload complete!`, "green");
  } else {
    const errorText = await response.text();
    notificationStore.addNotification(
      `File upload failed, ${errorText}. Please try again, or contact support.`,
      "red",
    );
  }
}

onMounted(async () => {
  if (vendorKey.value && vendorKey.value !== "all") {
    const response = await fetch(
      `/api/vendors/content/${vendorKey.value}/participants/last_updated/`,
    );
    const isoDate = await response.json();
    lastUploadDate.value = parseISO(isoDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
});
</script>
